/**
 * batch-61 loonstrook-dedupe (FO-456) - de INHOUD-vingerafdruk van een loonstrook.
 *
 * Probleem: de mailbox-import dedupliceerde alleen op de bytes van de PDF. Stuurt
 * de salarisverwerker dezelfde batch nog een keer (opnieuw gegenereerd, andere
 * PDF-metadata) of stuurt iemand de mail door, dan verschilt de checksum en werd
 * dezelfde strook een tweede rij - met FO-422 zelfs "run 2". Deze vingerafdruk
 * kijkt naar wat er OP de strook staat, onafhankelijk van de bytes:
 *
 *   BV/medewerker + jaar + periodetype + periodenummer + run-aanduiding (indien
 *   gelezen) + bruto + netto        -> loonstrook
 *   BV/medewerker + jaar + soort (+ bruto/netto indien gelezen) -> jaaropgave
 *
 * Gelijke vingerafdruk = dezelfde strook, ongeacht pad (mail, upload, wizard,
 * koppeling) of ingest. Twee legitieme runs met identieke bedragen onderscheiden
 * zich via de run-aanduiding op de strook; ontbreekt die, dan wint de dedupe
 * (spec §5.1, bewust) en kan de controleur handmatig "toch als extra run".
 *
 * NOOIT GOKKEN (spec §5.4): zonder identiteit of periode is er geen vingerafdruk
 * ("te beoordelen"); zonder bruto/netto alleen wanneer de run op de strook zelf
 * zeker is. De uitkomst is dan géén dedupe maar de gewone FO-422-keuzestap.
 *
 * Bedragen worden Decimal-exact genormaliseerd (2 decimalen, half-up, via de
 * BigInt-kern van `indexatie.ts`) - nooit via float. `betaaldatum` zit bewust
 * NIET in de sleutel: de extractie leest die niet, en een sleutel die bij
 * bestaande (gebackfillde) stroken zonder datum nooit kan matchen zou de dedupe
 * juist voor de bestaande dubbelen uitschakelen.
 *
 * De vingerafdruk is een sha256 over een canonieke sleutel, zodat er geen
 * loonbedrag leesbaar in de kolom belandt. Pure functie: geen DB, geen I/O.
 */

import type { LoonstrookPeriodeType, LoonstrookSoort } from '@streep/types'
import { naarTweeDecimalen } from './indexatie'
import { parseRunAanduiding } from './loonstrook-run'
import { sha256Hex } from './sha256'

/** Versie van de sleutel-opbouw; verhogen bij een niet-compatibele wijziging (+ backfill). */
export const LOONSTROOK_VINGERAFDRUK_VERSIE = 1

/**
 * Wiens strook is dit? Een gekoppelde strook draagt de medewerker (die
 * impliceert de BV). Een ongekoppelde strook is alleen te identificeren via het
 * personeelsnummer op de strook BINNEN een zeker afgeleide BV (nummers zijn per
 * BV/administratie uniek, niet over BV's heen).
 */
export type LoonstrookVingerafdrukIdentiteit =
  | { soort: 'MEDEWERKER'; employeeId: string }
  | { soort: 'PERSONEELSNUMMER'; entityId: string; personeelsnummer: string }

/** Bedrag zoals de caller het heeft: Prisma.Decimal (toString), decimaal-string of number. */
export type LoonstrookBedragWaarde = string | number | { toString(): string }

export interface LoonstrookVingerafdrukInput {
  identiteit: LoonstrookVingerafdrukIdentiteit | null
  soort: LoonstrookSoort
  jaar: number | null
  periodeType: LoonstrookPeriodeType | null
  maand: number | null
  periodeNummer: number | null
  /** De run-/verloningsaanduiding zoals gelezen op de strook, of null. */
  runAanduiding: string | null | undefined
  brutoLoon: LoonstrookBedragWaarde | null | undefined
  nettoLoon: LoonstrookBedragWaarde | null | undefined
}

export type LoonstrookVingerafdrukTeBeoordelenReden =
  /** Geen medewerker en geen zeker (BV + personeelsnummer)-paar. */
  | 'GEEN_IDENTITEIT'
  /** Jaar/periodetype/maand/periodenummer ontbreken of zijn inconsistent. */
  | 'GEEN_PERIODE'
  /** Loonstrook zonder bruto én netto, en de run op de strook is niet zeker. */
  | 'GEEN_BEDRAGEN_ZONDER_RUN'

export type LoonstrookVingerafdrukUitkomst =
  | {
      kind: 'bepaald'
      /** sha256-hex van `sleutel`; dit is wat op de rij staat en geïndexeerd wordt. */
      vingerafdruk: string
      /** De canonieke sleutel (voor tests/diagnose; bevat bedragen - niet loggen). */
      sleutel: string
      /** True wanneer de sleutel zonder bruto/netto is opgebouwd (alleen bij zekere run). */
      zonderBedragen: boolean
    }
  | { kind: 'te_beoordelen'; reden: LoonstrookVingerafdrukTeBeoordelenReden }

/** Personeelsnummer canoniek: trim, hoofdletters, leidende nullen weg bij een puur numeriek nummer. */
export function normaliseerPersoneelsnummer(raw: string | null | undefined): string | null {
  if (raw == null) return null
  const s = raw.replace(/\s+/g, '').toUpperCase()
  if (s.length === 0) return null
  if (/^\d+$/.test(s)) {
    const zonderNullen = s.replace(/^0+/, '')
    return zonderNullen.length === 0 ? '0' : zonderNullen
  }
  return s
}

/**
 * Bedrag canoniek als vaste 2-decimalen-string (Decimal-exact, half-up), of null
 * wanneer er geen (bruikbaar) bedrag is. Een number gaat via `toFixed(2)`
 * (float-inputs komen alleen uit de AI-extractie en zijn al 2-decimaal); alles
 * met een `toString()` (Prisma.Decimal, string) via de exacte BigInt-kern.
 *
 * Streng: alleen `-?cijfers[.cijfers]` of dezelfde vorm met één komma als
 * decimaalteken. Een notatie met duizendtallen ("1.234,56" / "1,234.56") is
 * dubbelzinnig en levert null - liever geen bedrag in de sleutel dan een
 * verkeerd bedrag (de callers geven Prisma.Decimal/number, nooit zo'n string).
 */
export function normaliseerBedrag(waarde: LoonstrookBedragWaarde | null | undefined): string | null {
  if (waarde == null) return null
  if (typeof waarde === 'number') {
    if (!Number.isFinite(waarde)) return null
    return naarTweeDecimalen(waarde.toFixed(2))
  }
  const ruw = String(waarde).trim()
  if (ruw.length === 0) return null
  let tekst = ruw
  if (/^-?\d+,\d+$/.test(ruw)) tekst = ruw.replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(tekst)) return null
  try {
    return naarTweeDecimalen(tekst)
  } catch {
    return null
  }
}

/**
 * Run-deel van de sleutel. Een leesbaar run-nummer wint (`r=2`); een aanduiding
 * die wel gelezen is maar geen nummer oplevert telt als tekst (`rt=...`), zodat
 * "nabetaling" en niets-gelezen niet samenvallen. Zonder aanduiding: `r=`.
 */
function runDeel(aanduiding: string | null | undefined): { deel: string; nummerZeker: boolean; gelezen: boolean } {
  const nummer = parseRunAanduiding(aanduiding)
  if (nummer != null) return { deel: `r=${nummer}`, nummerZeker: true, gelezen: true }
  const tekst = (aanduiding ?? '').toLowerCase().replace(/\s+/g, ' ').trim()
  if (tekst.length > 0) return { deel: `rt=${tekst}`, nummerZeker: false, gelezen: true }
  return { deel: 'r=', nummerZeker: false, gelezen: false }
}

function identiteitDeel(identiteit: LoonstrookVingerafdrukIdentiteit | null): string | null {
  if (!identiteit) return null
  if (identiteit.soort === 'MEDEWERKER') {
    const id = identiteit.employeeId.trim()
    return id.length > 0 ? `emp=${id}` : null
  }
  const entity = identiteit.entityId.trim()
  const pnr = normaliseerPersoneelsnummer(identiteit.personeelsnummer)
  if (entity.length === 0 || !pnr) return null
  return `pnr=${entity}:${pnr}`
}

/**
 * Bereken de inhoud-vingerafdruk. Zie de module-header voor de regels.
 */
export function berekenLoonstrookVingerafdruk(
  input: LoonstrookVingerafdrukInput,
): LoonstrookVingerafdrukUitkomst {
  const identiteit = identiteitDeel(input.identiteit)
  if (!identiteit) return { kind: 'te_beoordelen', reden: 'GEEN_IDENTITEIT' }

  if (input.jaar == null || !Number.isInteger(input.jaar)) {
    return { kind: 'te_beoordelen', reden: 'GEEN_PERIODE' }
  }

  let periodeDeel: string
  if (input.soort === 'JAAROPGAVE') {
    // Eén jaaropgave per jaar (FO-422 A3): de periode is het jaar zelf.
    periodeDeel = 'pt=|m=|p='
  } else {
    if (input.periodeType === 'MONTH') {
      if (input.maand == null || !Number.isInteger(input.maand)) {
        return { kind: 'te_beoordelen', reden: 'GEEN_PERIODE' }
      }
      periodeDeel = `pt=MONTH|m=${input.maand}|p=`
    } else if (input.periodeType === 'FOUR_WEEK') {
      if (input.periodeNummer == null || !Number.isInteger(input.periodeNummer)) {
        return { kind: 'te_beoordelen', reden: 'GEEN_PERIODE' }
      }
      periodeDeel = `pt=FOUR_WEEK|m=|p=${input.periodeNummer}`
    } else {
      return { kind: 'te_beoordelen', reden: 'GEEN_PERIODE' }
    }
  }

  const run = runDeel(input.runAanduiding)
  const bruto = normaliseerBedrag(input.brutoLoon)
  const netto = normaliseerBedrag(input.nettoLoon)
  const zonderBedragen = bruto == null && netto == null

  // Loonstrook zonder enig bedrag: alleen wanneer de run op de strook ZEKER is
  // (leesbaar nummer). Anders is er te weinig om "dezelfde strook" op te
  // baseren - dan liever de FO-422-keuzestap dan een verkeerde dedupe.
  if (zonderBedragen && input.soort !== 'JAAROPGAVE' && !run.nummerZeker) {
    return { kind: 'te_beoordelen', reden: 'GEEN_BEDRAGEN_ZONDER_RUN' }
  }

  const sleutel = [
    `v${LOONSTROOK_VINGERAFDRUK_VERSIE}`,
    identiteit,
    `soort=${input.soort}`,
    `jaar=${input.jaar}`,
    periodeDeel,
    run.deel,
    `b=${bruto ?? ''}`,
    `n=${netto ?? ''}`,
  ].join('|')

  return { kind: 'bepaald', vingerafdruk: sha256Hex(sleutel), sleutel, zonderBedragen }
}

/**
 * Mail-niveau (spec §3.4): de "bijlage-set" van een ingest is de gesorteerde,
 * ontdubbelde verzameling per-pagina-identiteiten (vingerafdruk waar bepaald,
 * anders `bytes:<checksum>`). Twee mails met dezelfde set zijn dezelfde
 * aanlevering, ook met een nieuwe graphMessageId (doorgestuurd/opnieuw
 * verzonden). Leeg -> null (een mail zonder pagina's heeft geen set).
 */
export function berekenBijlagenSetHash(identiteiten: readonly string[]): string | null {
  const uniek = Array.from(new Set(identiteiten.map((s) => s.trim()).filter((s) => s.length > 0)))
  if (uniek.length === 0) return null
  uniek.sort()
  return sha256Hex(`set-v${LOONSTROOK_VINGERAFDRUK_VERSIE}\n${uniek.join('\n')}`)
}

/** Pagina-identiteit voor de set: de vingerafdruk als die er is, anders de bytes. */
export function bijlageIdentiteit(vingerafdruk: string | null, checksum: string): string {
  return vingerafdruk ? `fp:${vingerafdruk}` : `bytes:${checksum}`
}
